import type { ReactNode } from "react";
import { Mail, Phone } from "lucide-react";
import { cn } from "@/lib/utils";

interface ClientInfoCardProps {
  client?: any | null;
  partnerName?: string | null;
  partnerBusinessType?: string | null;
  footer?: ReactNode;
  className?: string;
  showPlaceholder?: boolean;
}

export function ClientInfoCard({
  client,
  partnerName,
  partnerBusinessType,
  footer,
  className,
  showPlaceholder = false,
}: ClientInfoCardProps) {
  if (!client) {
    if (!showPlaceholder) return null;

    return (
      <div className="mt-1 rounded-md border border-dashed p-3 text-sm text-muted-foreground">
        Client not set
      </div>
    );
  }

  const firstName = client.first_name ?? client.firstName ?? "";
  const lastName = client.last_name ?? client.lastName ?? "";
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName = client.company || fullName;
  const personName = fullName || displayName || "";
  const agencyName =
    client.operator_agency_name ||
    client.operatorAgencyName ||
    client.operator_agent_name ||
    client.operatorAgentName ||
    "";
  const phone = client.mobile || client.alternate_phone || client.alternatePhone || "";
  const email = client.email || "";

  const rawPartnerId = client.partner_id ?? client.partnerId;
  const parsedPartnerId = typeof rawPartnerId === "number" ? rawPartnerId : Number(rawPartnerId);
  const hasPartnerId = Number.isFinite(parsedPartnerId);
  const resolvedPartnerName = partnerName || agencyName;
  const cameFromPartner = hasPartnerId || Boolean(resolvedPartnerName);
  const partnerType = (
    partnerBusinessType ??
    client.partner_business_type ??
    client.partnerBusinessType ??
    ""
  )
    .toString()
    .toLowerCase();

  const normalizedPartnerType =
    partnerType === "operator" ? "operator" : partnerType === "agency" || partnerType === "agent" ? "agency" : null;
  const profileType: "agency" | "operator" | "final_client" | "partner" = cameFromPartner
    ? (normalizedPartnerType ?? "partner")
    : "final_client";
  const profileLabel = profileType === "agency"
    ? "Agency"
    : profileType === "operator"
      ? "Operator"
      : profileType === "partner"
        ? "Partner"
        : "Client Final";
  const initials = (personName || displayName || "Client")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join("") || "CL";
  const partnerBadgeTone =
    profileType === "operator"
      ? "border-emerald-300/80 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-200"
      : profileType === "agency"
        ? "border-sky-300/80 bg-sky-100 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/15 dark:text-sky-200"
        : "border-indigo-300/80 bg-indigo-100 text-indigo-800 dark:border-indigo-500/40 dark:bg-indigo-500/15 dark:text-indigo-200";

  return (
    <div
      id="client_info"
      className={cn(
        "group mt-1 rounded-2xl border border-border/80 bg-gradient-to-br from-card to-muted/40 p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md md:p-5",
        className
      )}
    >
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Client</div>

      <div className="flex items-start gap-3 rounded-xl border border-border/80 bg-card/90 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-300/80 bg-emerald-100 text-sm font-bold text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-200">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-2xl font-semibold text-foreground">
            {personName || displayName || "Client"}
          </div>
          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
            {phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <span className="truncate">{phone}</span>
              </div>
            )}
            {email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span className="truncate">{email}</span>
              </div>
            )}
            {!email && !phone && <div className="text-muted-foreground">No contact details</div>}
          </div>
        </div>
      </div>

      <div className="mt-3">
        {cameFromPartner ? (
          <div className="flex flex-col gap-2 rounded-xl border border-border/80 bg-muted/50 px-3 py-2 text-sm text-foreground/90 md:flex-row md:items-center">
            <span className="font-medium text-muted-foreground">Referred by:</span>
            <div className="flex min-w-0 items-center gap-2">
              <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${partnerBadgeTone}`}>
                {profileLabel}
              </span>
              <span className="truncate font-semibold text-foreground">{resolvedPartnerName || "Partner"}</span>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-amber-300/80 bg-amber-100/80 px-3 py-2 text-sm font-medium text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200">
            Client Final
          </div>
        )}
      </div>

      {footer && (
        <div className="mt-3 rounded-xl border border-border/80 bg-card/70 px-3 py-2.5">
          {footer}
        </div>
      )}
    </div>
  );
}
